import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form } from 'semantic-ui-react';
import { useEffect, useMemo, useState } from 'react';
import { Info } from '@phosphor-icons/react';
import styled from 'styled-components';
import { DarkPopup, SkupremeModal, LoaderBar } from '../../../components';
import {
  CheckboxFormField,
  ControlledPhoneFormField,
  ControlledTextFormField,
  DropdownFormField,
  HoverBorderButton,
  StyledButton,
} from '../../../elements';
import {
  CommonUtility,
  OrderService,
  ToastMessage,
  tooltipTexts,
} from '../../../utility';
import { OrganizationRegions } from '../../../hooks';
import { dropdownOptionsIncludeFlag } from '../../../elements/Flag';
import { LabelSelectionPopup } from './label-selection-popup';

const InlineContainer = styled.div`
  display: inline-flex;
`;

const ItemSchema = yup.object().shape(
  {
    name: yup.string().required('Name is Required'),
    email: yup.string().email().optional(),
    phone: yup
      .string()
      .nullable()
      .test('valid-phone', 'Invalid phone number', function (value) {
        const { dialCodeLength } = this.options.context || {
          dialCodeLength: 0,
        };

        if (!value || !value.trim()) return true; // allow empty
        const digits = value.replace(/\D/g, '').length;

        if (digits < 6 + dialCodeLength) {
          return this.createError({
            message: `Phone Number must be at least ${6 + dialCodeLength} digits`,
          });
        }
        if (digits > 12 + dialCodeLength) {
          return this.createError({
            message: `Phone Number cannot be more than ${12 + dialCodeLength} digits`,
          });
        }
        return true;
      }),
    addressLine1: yup.string().required('Address Line 1 is Required'),
    addressLine2: yup.string().optional(),
    city: yup.string().required('City is Required'),
    postalCode: yup.string().required('Zip Code is Required'),
    state: yup
      .string()
      .required('State is Required')
      .min(2, 'Invalid State Code')
      .max(2, 'Invalid State Code'),
    country: yup.string().required('Country is Required'),
    regenerateLabel: yup.boolean().default(true),
  },
  ['phone', 'phone'],
);

export function EditAddressPopup({
  address,
  open,
  onClose,
  orderId,
  refresh,
  labels = [],
}) {
  const [loading, setLoading] = useState(false);
  const [dialCodeLength, setDialCodeLength] = useState(1);
  const [showLabelSelection, setShowLabelSelection] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const { data: regions } = OrganizationRegions();

  const countriesList = useMemo(() => {
    if (CommonUtility.isValidArray(regions?.data)) {
      return dropdownOptionsIncludeFlag(
        regions?.data?.map(x => ({ ...x, text: x.region_alias, value: x._id })),
      );
    }
    return [];
  }, [regions]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(ItemSchema),
    context: { dialCodeLength }, // ✅ pass context here
    shouldUnregister: true,
    mode: 'onChange',
  });

  const watchFields = watch();

  const onSubmit = async formValues => {
    // If regenerateLabel is checked and we have labels, show selection popup
    if (formValues.regenerateLabel && labels.length > 0) {
      setPendingFormValues(formValues);
      setShowLabelSelection(true);
      return;
    }

    // Otherwise proceed with address update
    await handleAddressUpdate(formValues);
  };

  const handleAddressUpdate = async (formValues, selectedIds = []) => {
    try {
      setLoading(true);
      const updateData = {
        ...formValues,
      };

      // If labels are selected, include them as array
      if (selectedIds.length > 0) {
        updateData.labelIds = selectedIds;
      }
      // If no labels selected, don't include labelIds (will regenerate all if regenerateLabel is true)

      await OrderService.updateAddress(orderId, updateData);
      ToastMessage.success('Address updated successfully');
      setShowLabelSelection(false);
      setSelectedLabelIds([]);
      setPendingFormValues(null);
      onClose();
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLabelSelectionConfirm = selectedIds => {
    setSelectedLabelIds(selectedIds);
    setShowLabelSelection(false);
    handleAddressUpdate(pendingFormValues, selectedIds);
  };

  useEffect(() => {
    let countryToSet = address?.countryCode;

    // set the _id from list not code like "US"
    if (countryToSet && countryToSet?.length === 2) {
      countryToSet = countriesList?.find(x => x.name === countryToSet)?._id;
    }

    reset({
      country: countryToSet || '',
      phone: address?.phoneNumber || '',
      state: address?.stateOrRegion || '',
      regenerateLabel: true, // Default to checked
      ...(address || {}),
    });
  }, [address, countriesList]);

  const currentCountryCode = useMemo(() => {
    if (watchFields.country) {
      return countriesList?.find(x => x.value === watchFields.country)?.name;
    }
    return 'us';
  }, [countriesList, watchFields.country]);

  return (
    <SkupremeModal
      size="tiny"
      open={open}
      onClose={loading ? () => {} : onClose}
      title="Edit Address"
    >
      {loading && <LoaderBar />}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ControlledTextFormField
          error={errors.addressLine1}
          hint={errors.addressLine1?.message}
          label="Address Line 1"
          name="addressLine1"
          control={control}
          required
          disabled={loading}
        />
        <ControlledTextFormField
          error={errors.addressLine2}
          hint={errors.addressLine2?.message}
          label="Address Line 2"
          name="addressLine2"
          control={control}
          disabled={loading}
        />
        <Form.Group widths="equal">
          <ControlledTextFormField
            error={errors.city}
            hint={errors.city?.message}
            label="City"
            name="city"
            control={control}
            required
            disabled={loading}
          />
          <ControlledTextFormField
            error={errors.state}
            label={
              <InlineContainer className="align-items-center">
                State (Abbreviated)
                <DarkPopup trigger={<Info />} hoverable position="top center">
                  {tooltipTexts.stateInfo}
                </DarkPopup>
              </InlineContainer>
            }
            name="state"
            placeholder="State"
            control={control}
            required
            hint={errors?.state?.message}
            disabled={loading}
          />
        </Form.Group>
        <Form.Group widths="equal">
          <ControlledTextFormField
            error={errors.postalCode}
            hint={errors.postalCode?.message}
            label="Zip Code"
            name="postalCode"
            control={control}
            required
            disabled={loading}
          />
          <DropdownFormField
            options={countriesList}
            name="country"
            error={errors.country}
            hint={errors.country?.message}
            label="Country"
            control={control}
            defaultValue=""
            required
            disabled={loading}
          />
        </Form.Group>
        <Form.Group widths="equal">
          <ControlledTextFormField
            error={errors.name}
            hint={errors.name?.message}
            label="Customer Name"
            name="name"
            control={control}
            required
            disabled={loading}
          />
          <ControlledTextFormField
            error={errors.email}
            hint={errors.email?.message}
            label="Email Address"
            name="email"
            control={control}
            disabled={loading}
          />
        </Form.Group>
        <Form.Group widths="equal">
          <ControlledPhoneFormField
            error={errors.phone}
            label="Mobile Number"
            name="phone"
            control={control}
            country={currentCountryCode.toLowerCase()}
            setDialCodeLength={setDialCodeLength}
            hint={errors.phone?.message}
            disabled={loading}
          />
        </Form.Group>
        <CheckboxFormField
          control={control}
          name="regenerateLabel"
          text="Regenerate Label"
          defaultValue={true}
          disabled={loading}
        />
        <div className="d-flex justify-content-end">
          <HoverBorderButton
            onClick={onClose}
            disabled={loading}
            type="button"
            className="mr-2"
          >
            Close
          </HoverBorderButton>
          <StyledButton loading={loading} type="confirm">
            Confirm
          </StyledButton>
        </div>
      </Form>
      <LabelSelectionPopup
        open={showLabelSelection}
        onClose={() => {
          setShowLabelSelection(false);
          setPendingFormValues(null);
        }}
        labels={labels}
        onConfirm={handleLabelSelectionConfirm}
        loading={loading}
      />
    </SkupremeModal>
  );
}
